<template>
  <div class="sign-doc-container">
    <!-- Loading Overlay -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <p class="loading-text">Loading PDF, please wait...</p>
      </div>
    </div>

    <!-- Upload Progress Overlay -->
    <div v-if="isUploading" class="upload-overlay">
      <div class="upload-content">
        <div class="upload-spinner"></div>
        <p class="upload-text">Uploading signed document...</p>
      </div>
    </div>

    <!-- Main Content -->
    <div class="document-viewer" ref="documentViewer">
      <!-- Hidden PDF Canvas -->
      <canvas ref="pdfCanvas" style="display: none"></canvas>

      <!-- PDF Pages -->
      <div
        v-for="(pageConfig, index) in pdfImageConfig"
        :key="'page-' + index"
        class="pdf-page"
        :style="{ transform: `scale(${zoomLevel})` }"
      >
        <div class="page-container" :class="{ 'allow-touch': !isPenActive }">
          <v-stage
            :ref="'stage' + index"
            :config="getStageConfig(index)"
            @mousedown="handleMouseDown($event, index)"
            @touchstart="handleTouchStart($event, index)"
            @mousemove="handleMouseMove($event, index)"
            @touchmove="handleTouchMove($event, index)"
            @mouseup="handleMouseUp($event, index)"
            @touchend="handleTouchEnd($event, index)"
            @mouseleave="handleMouseLeave($event, index)"
            @touchleave="handleTouchLeave($event, index)"
            @pointerleave="handlePointerLeave($event, index)"
            @wheel="handleWheel($event)"
          >
            <v-layer>
              <!-- PDF Page Image -->
              <v-image :key="'page-image-' + index" :config="pageConfig" />

              <!-- Drawing Lines -->
              <v-line
                v-for="(line, lineIndex) in lines[index] || []"
                :key="`page-${index}-line-${lineIndex}`"
                :config="line"
              />
            </v-layer>
          </v-stage>

          <!-- Page Number -->
          <div class="page-number">
            Page {{ index + 1 }} of {{ pdfImageConfig.length }}
          </div>
        </div>
      </div>
    </div>

    <!-- Floating Action Buttons -->
    <div class="fab-container">
      <!-- Main Action Buttons -->
      <div class="action-buttons">
        <div class="drawing-mode-switch" @click="toggleDrawingMode">
          <div class="switch-option" :class="{ active: !eraserMode }">
            <i class="fas fa-pen"></i>
          </div>
          <div class="switch-option" :class="{ active: eraserMode }">
            <i class="fas fa-eraser"></i>
          </div>
          <div class="switch-slider" :class="{ 'slide-right': eraserMode }"></div>
        </div>

        <button
          class="fab-btn print-btn"
          @click="printDocument"
          title="Print Document"
        >
          <i class="fas fa-print"></i>
        </button>

        <button
          class="fab-btn upload-btn"
          @click="uploadDocument"
          :disabled="isUploading"
          title="Upload Signed Document"
        >
          <i class="fas fa-upload"></i>
        </button>
      </div>

      <!-- Zoom Controls -->
      <div class="zoom-controls">
        <button
          class="zoom-btn"
          @click="zoomIn"
          :disabled="zoomLevel >= maxZoom"
          title="Zoom In"
        >
          <i class="fas fa-plus"></i>
        </button>
        <span class="zoom-level">{{ Math.round(zoomLevel * 100) }}%</span>
        <button
          class="zoom-btn"
          @click="zoomOut"
          :disabled="zoomLevel <= minZoom"
          title="Zoom Out"
        >
          <i class="fas fa-minus"></i>
        </button>
        <button class="zoom-btn" @click="resetZoom" title="Reset Zoom">
          <i class="fas fa-expand-arrows-alt"></i>
        </button>
      </div>
    </div>

    <!-- Close Button -->
    <button class="close-btn" @click="closeDocument" title="Close Document">
      <i class="fas fa-times"></i>
    </button>

    <!-- Drawing Tools Indicator -->
    <div class="drawing-indicator" v-if="isPenActive" :class="{ 'eraser-mode': eraserMode }">
      <i :class="eraserMode ? 'fas fa-eraser' : 'fas fa-pen'"></i>
      <span>{{ eraserMode ? 'Eraser Mode' : 'Apple Pencil Ready' }}</span>
    </div>

    <!-- Dialog Component -->
    <Dialog ref="Dialog"></Dialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import Vue from "vue";
import VueKonva from "vue-konva";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import jsPDF from "jspdf";
import Dialog from "@/components/modal/Dialog";
import { eventBus } from "@/main";
import DrawingMixin from "@/mixins/DrawingMixin";

Vue.use(VueKonva);
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default {
  name: "SignDoc",
  components: { Dialog },
  mixins: [DrawingMixin],

  data() {
    return {
      // PDF Data
      pdfUrl: null,
      docId: null,
      patientId: null,
      clinicUrl: null,
      branchUrl: null,
      filename: null,
      templateId: null,
      pdfImageConfig: [],

      // UI State
      loading: false,
      isUploading: false,
      isPenActive: false,

      // Zoom & Pan
      zoomLevel: 1,
      minZoom: 0.5,
      maxZoom: 3,
      panOffset: { x: 0, y: 0 },

      // Stage Configuration
      stageConfig: {
        width: 800,
        height: 1200,
      },
    };
  },

  mounted() {
    this.initializeComponent();
    this.setupEarlyTouchDetection();
  },

  beforeDestroy() {
    this.cleanup();
  },

  methods: {
    ...mapActions({
      createMediaFiles: "moduleMediaFile/createMediaFiles",
    }),

    // === INITIALIZATION ===
    async initializeComponent() {
      // Initialize drawing functionality
      this.initializeDrawing();
      
      // Setup event listeners
      eventBus.$on("renderPDF", this.renderPDF);

      // Parse route query parameters
      this.parseRouteParams();

      // Render PDF if URL is provided
      if (this.pdfUrl) {
        await this.renderPDF(this.pdfUrl);
      }
    },

    parseRouteParams() {
      const query = this.$route.query;
      if (query) {
        this.pdfUrl = query.src;
        this.docId = parseInt(query.id);
        this.patientId = parseInt(query.patientId || query.patient);
        this.clinicUrl = query.clinicUrl || query.clinic;
        this.branchUrl = query.branchUrl || query.branch;
        this.filename = query.filename;
        this.templateId = query.template;
      }
    },

    // === PDF RENDERING ===
    async renderPDF(pdfUrl) {
      this.loading = true;
      try {
        const loadingTask = pdfjsLib.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        const numPages = pdf.numPages;

        this.pdfImageConfig = [];

        for (let pageNumber = 1; pageNumber <= numPages; pageNumber++) {
          const page = await pdf.getPage(pageNumber);
          // Dynamic scale based on device pixel ratio for optimal quality
          const deviceRatio = window.devicePixelRatio || 1;
          const scale = Math.min(deviceRatio * 2.5, 6); // Max 6x for performance balance
          const viewport = page.getViewport({ scale });

          const canvas = this.$refs.pdfCanvas;
          const context = canvas.getContext("2d");
          
          // Enhanced canvas rendering for crisp quality
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          
          // Improve rendering quality
          context.imageSmoothingEnabled = false; // Prevent blur from smoothing
          context.textRenderingOptimization = 'optimizeSpeed';

          await page.render({
            canvasContext: context,
            viewport: viewport,
          }).promise;

          // Convert to image
          const image = new window.Image();
          image.src = canvas.toDataURL("image/png");

          await new Promise(resolve => {
            image.onload = () => {
              const aspectRatio = canvas.width / canvas.height;
              const stageWidth = this.stageConfig.width;
              const stageHeight = stageWidth / aspectRatio;

              this.pdfImageConfig.push({
                image: image,
                x: 0,
                y: 0,
                width: stageWidth,
                height: stageHeight,
              });

              // Initialize lines array for this page
              this.initializeLinesForPage(pageNumber - 1);

              resolve();
            };
          });
        }
        
        // Set initial stage listening state after all pages are rendered
        this.$nextTick(() => {
          this.updateStageListening();
        });
      } catch (error) {
        console.error("Error rendering PDF:", error);
        this.$refs.Dialog.showAlertInfo("Error loading PDF", null, "error");
      } finally {
        this.loading = false;
      }
    },

    // === STAGE CONFIGURATION ===
    getStageConfig(pageIndex) {
      const pageConfig = this.pdfImageConfig[pageIndex];
      const baseConfig = {
        width: pageConfig ? pageConfig.width : this.stageConfig.width,
        height: pageConfig ? pageConfig.height : this.stageConfig.height,
      };
      return this.getDrawingStageConfig(baseConfig);
    },

    // === EARLY TOUCH DETECTION ===
    setupEarlyTouchDetection() {
      const container = this.$refs.documentViewer;
      if (container) {
        container.addEventListener('touchstart', this.detectTouchTypeEarly, { 
          passive: true, 
          capture: true
        });
      }
    },

    detectTouchTypeEarly(e) {
      const touch = e.touches?.[0];
      const isApplePencil = touch?.touchType === 'stylus' || touch?.force > 0;
      
      this.isPenActive = isApplePencil;
      
      if (isApplePencil) {
        this.$nextTick(() => {
          const konvaContent = document.querySelector('.konvajs-content');
          const canvas = document.querySelector('.konvajs-content canvas');
          
          if (konvaContent) konvaContent.style.pointerEvents = 'auto';
          if (canvas) canvas.style.pointerEvents = 'auto';
        });
      } else {
        this.$nextTick(() => {
          const canvas = document.querySelector('.konvajs-content canvas');
          const konvaContent = document.querySelector('.konvajs-content');
          
          if (canvas && getComputedStyle(canvas).pointerEvents !== 'none') {
            if (konvaContent) konvaContent.style.pointerEvents = 'none';
            if (canvas) canvas.style.pointerEvents = 'none';
          }
        });
      }
    },

    // === TOUCH HANDLING OVERRIDES ===
    handleTouchStart(e, pageIndex) {
      if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
        return;
      }

      const isApplePencil = this.detectApplePencilFromTouch(e);
      this.isPenActive = isApplePencil;

      if (isApplePencil) {
        e.evt.preventDefault();
        e.evt.stopPropagation();
        if (this.eraserMode) {
          this.startObjEraser(e, pageIndex);
        } else {
          this.startDrawing(e, pageIndex);
        }
      }
    },

    handleTouchMove(e, pageIndex) {
      if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
        return;
      }

      const isApplePencil = this.detectApplePencilFromTouch(e);
      this.isPenActive = isApplePencil;

      if (this.isDrawing && isApplePencil) {
        e.evt.preventDefault();
        
        if (this.eraserMode) {
          this.continueDrawing(e, pageIndex);
          this.objErasing(pageIndex);
        } else {
          this.continueDrawing(e, pageIndex);
        }
      }
    },

    handleTouchEnd(e, pageIndex) {
      this.isPenActive = false;

      if (this.isDrawing) {
        if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
          return;
        }

        if (this.eraserMode) {
          // Use nextTick to ensure proper timing for eraser cleanup
          this.$nextTick(() => {
            if (this.lines && this.lines[pageIndex] && this.lines[pageIndex].length > 0) {
              // Only pop if the last line is an eraser line (gainsboro stroke with strokeWidth 5)
              const lastLine = this.lines[pageIndex][this.lines[pageIndex].length - 1];
              if (lastLine && lastLine.stroke === 'gainsboro' && lastLine.strokeWidth === 5) {
                this.lines[pageIndex].pop();
              }
            }
            
            if (this.linesEdited) {
              if (this.addHistory) this.addHistory();
              this.linesEdited = false;
            }
          });
        } else {
          this.finishDrawing(pageIndex);
        }

        if (typeof window !== 'undefined') {
          window.lastTouchEventTime = Date.now();
        }
      }
    },

    handleTouchLeave(e, pageIndex) {
      this.isPenActive = false;

      if (this.isDrawing) {
        if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
          this.isDrawing = false;
          this.currentDrawing = null;
          return;
        }

        if (this.eraserMode) {
          // Use nextTick to ensure proper timing for eraser cleanup
          this.$nextTick(() => {
            if (this.lines && this.lines[pageIndex] && this.lines[pageIndex].length > 0) {
              // Only pop if the last line is an eraser line (gainsboro stroke with strokeWidth 5)
              const lastLine = this.lines[pageIndex][this.lines[pageIndex].length - 1];
              if (lastLine && lastLine.stroke === 'gainsboro' && lastLine.strokeWidth === 5) {
                this.lines[pageIndex].pop();
              }
            }
            
            if (this.linesEdited) {
              if (this.addHistory) this.addHistory();
              this.linesEdited = false;
            }
          });
        } else {
          this.finishDrawing(pageIndex);
        }

        this.isDrawing = false;
        this.currentDrawing = null;
      }
    },

    // === MOUSE HANDLING OVERRIDES ===
    handleMouseDown(e, pageIndex) {
      if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
        return;
      }

      // Always set isPenActive for mouse events (assume it's drawing intent)
      this.isPenActive = true;

      if (this.eraserMode) {
        this.startObjEraser(e, pageIndex);
      } else {
        this.startDrawing(e, pageIndex);
      }
    },

    handleMouseMove(e, pageIndex) {
      if (this.isDrawing) {
        if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
          return;
        }

        if (this.eraserMode) {
          this.continueDrawing(e, pageIndex);
          this.objErasing(pageIndex);
        } else {
          this.continueDrawing(e, pageIndex);
        }
      }
    },

    handleMouseUp(e, pageIndex) {
      this.isPenActive = false;

      if (this.isDrawing) {
        if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
          return;
        }

        if (this.eraserMode) {
          // Use nextTick to ensure proper timing for eraser cleanup
          this.$nextTick(() => {
            if (this.lines && this.lines[pageIndex] && this.lines[pageIndex].length > 0) {
              // Only pop if the last line is an eraser line (gainsboro stroke with strokeWidth 5)
              const lastLine = this.lines[pageIndex][this.lines[pageIndex].length - 1];
              if (lastLine && lastLine.stroke === 'gainsboro' && lastLine.strokeWidth === 5) {
                this.lines[pageIndex].pop();
              }
            }
            
            if (this.linesEdited) {
              if (this.addHistory) this.addHistory();
              this.linesEdited = false;
            }
          });
        } else {
          this.finishDrawing(pageIndex);
        }
      }
    },

    handleMouseLeave(e, pageIndex) {
      this.isPenActive = false;

      if (this.isDrawing) {
        if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
          this.isDrawing = false;
          this.currentDrawing = null;
          return;
        }

        if (this.eraserMode) {
          // Use nextTick to ensure proper timing for eraser cleanup
          this.$nextTick(() => {
            if (this.lines && this.lines[pageIndex] && this.lines[pageIndex].length > 0) {
              // Only pop if the last line is an eraser line (gainsboro stroke with strokeWidth 5)
              const lastLine = this.lines[pageIndex][this.lines[pageIndex].length - 1];
              if (lastLine && lastLine.stroke === 'gainsboro' && lastLine.strokeWidth === 5) {
                this.lines[pageIndex].pop();
              }
            }
            
            if (this.linesEdited) {
              if (this.addHistory) this.addHistory();
              this.linesEdited = false;
            }
          });
        } else {
          this.finishDrawing(pageIndex);
        }

        this.isDrawing = false;
        this.currentDrawing = null;
      }
    },

    handlePointerLeave(e, pageIndex) {
      this.isPenActive = false;

      if (this.isDrawing) {
        if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
          this.isDrawing = false;
          this.currentDrawing = null;
          return;
        }

        if (this.eraserMode) {
          // Use nextTick to ensure proper timing for eraser cleanup
          this.$nextTick(() => {
            if (this.lines && this.lines[pageIndex] && this.lines[pageIndex].length > 0) {
              // Only pop if the last line is an eraser line (gainsboro stroke with strokeWidth 5)
              const lastLine = this.lines[pageIndex][this.lines[pageIndex].length - 1];
              if (lastLine && lastLine.stroke === 'gainsboro' && lastLine.strokeWidth === 5) {
                this.lines[pageIndex].pop();
              }
            }
            
            if (this.linesEdited) {
              if (this.addHistory) this.addHistory();
              this.linesEdited = false;
            }
          });
        } else {
          this.finishDrawing(pageIndex);
        }

        this.isDrawing = false;
        this.currentDrawing = null;
      }
    },

    // === ZOOM & PAN ===
    handleWheel(e) {
      // Only allow zoom with Ctrl/Cmd key to prevent accidental zooming
      if (e.evt.ctrlKey || e.evt.metaKey) {
        e.evt.preventDefault();

        const delta = e.evt.deltaY;
        if (delta < 0) {
          this.zoomIn();
        } else {
          this.zoomOut();
        }
      }
    },

    zoomIn() {
      this.zoomLevel = Math.min(this.zoomLevel + 0.1, this.maxZoom);
    },

    zoomOut() {
      this.zoomLevel = Math.max(this.zoomLevel - 0.1, this.minZoom);
    },

    resetZoom() {
      this.zoomLevel = 1;
    },

    // === STAGE LISTENING CONTROL ===
    /**
     * Updates stage listening state based on drawing mode
     * Controls whether stages respond to touch/mouse events
     */
    updateStageListening() {
      // Update listening state for all stages
      this.pdfImageConfig.forEach((_, pageIndex) => {
        const stage = this.$refs[`stage${pageIndex}`];
        if (stage && stage[0]) {
          const stageNode = stage[0].getStage();
          if (stageNode) {
            // Enable listening when in drawing mode, disable when not
            stageNode.listening(true);
          }
        }
      });
    },

    // === ACTION METHODS ===
    /**
     * Toggles between writing mode (pen) and erasing mode
     */
    toggleDrawingMode() {
      this.eraserMode = !this.eraserMode;
      // Update stage listening when drawing mode changes
      this.updateStageListening();
    },

    async uploadDocument() {
      this.isUploading = true;
      try {
        await this.exportToPDF();
        this.$refs.Dialog.showAlertInfo(
          "Upload Successful",
          "Document has been uploaded successfully.",
          "success"
        );
      } catch (error) {
        console.error("Upload error:", error);
        this.$refs.Dialog.showAlertInfo(
          "Upload Failed",
          "Failed to upload document. Please try again.",
          "error"
        );
      } finally {
        this.isUploading = false;
      }
    },

    async printDocument() {
      try {
        await this.exportToPDF({ isPrint: true });
      } catch (error) {
        console.error("Print error:", error);
        this.$refs.Dialog.showAlertInfo(
          "Print Failed",
          "Failed to prepare document for printing.",
          "error"
        );
      }
    },

    async closeDocument() {
      const result = await this.$refs.Dialog.showAlertConfirm(
        "ปิดเอกสาร",
        "คุณแน่ใจหรือไม่ที่จะปิดเอกสารนี้?",
        null,
        "ปิด",
        "ยกเลิก"
      );

      if (result.value) {
        window.close();
      }
    },

    // === PDF EXPORT ===
    async exportToPDF({ isPrint = false, isDownload = false } = {}) {
      if (this.pdfImageConfig.length === 0) {
        throw new Error("No PDF pages to export");
      }

      const pixelRatio = 2.0;
      let pdf = null;

      // Process all pages
      const imagePromises = this.pdfImageConfig.map(async (config, index) => {
        const stageRef = this.$refs[`stage${index}`];
        if (!stageRef || !stageRef[0]) {
          throw new Error(`Stage ${index} not found`);
        }

        const stage = stageRef[0].getStage();
        const dataURL = stage.toDataURL({ pixelRatio });

        return new Promise(resolve => {
          const img = new Image();
          img.onload = () => {
            resolve({
              dataURL,
              width: stage.width(),
              height: stage.height(),
            });
          };
          img.src = dataURL;
        });
      });

      const imageDataArray = await Promise.all(imagePromises);

      // Create PDF
      const { width: pdfWidth, height: pdfHeight } = imageDataArray[0];
      pdf = new jsPDF({
        orientation: pdfHeight > pdfWidth ? "portrait" : "landscape",
        unit: "pt",
        format: [pdfWidth, pdfHeight],
      });

      // Add pages to PDF
      imageDataArray.forEach((imageData, index) => {
        if (index > 0) {
          pdf.addPage([imageData.width, imageData.height]);
        }
        pdf.addImage(
          imageData.dataURL,
          "PNG",
          0,
          0,
          imageData.width,
          imageData.height
        );
      });

      // Handle different export types
      if (isPrint) {
        this.handlePrintPDF(pdf);
      } else if (isDownload) {
        pdf.save(`${this.filename || "signed-document"}.pdf`);
      } else {
        await this.handleUploadPDF(pdf);
      }
    },

    /**
     * Handles PDF printing with cross-platform support
     * @param {Object} pdf - jsPDF instance
     */
    handlePrintPDF(pdf) {
      // Create blob URL for better compatibility
      const pdfBlob = pdf.output("blob");
      const pdfBlobUrl = URL.createObjectURL(pdfBlob);
      
      // Platform detection
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isIOS || isMobile) {
        // For mobile devices, open in new tab
        this.printForMobile(pdfBlobUrl);
      } else {
        // For desktop, use clean print dialog
        this.printForDesktop(pdfBlobUrl, pdfBlob);
      }
    },

    printForMobile(pdfDataUrl) {
      // Open PDF in new tab for mobile printing
      const printWindow = window.open(pdfDataUrl, '_blank');
      if (!printWindow) {
        // Fallback: show download link if popup blocked
        this.$refs.Dialog.showAlertInfo(
          "Print Blocked",
          "Please allow popups to print, or download the PDF to print manually.",
          "warning"
        );
      }
    },

    printForDesktop(pdfBlobUrl, pdfBlob) {
      // Create print overlay that shows PDF properly
      const overlay = document.createElement("div");
      overlay.className = "print-overlay";
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.9);
        z-index: 2000;
        display: flex;
        align-items: center;
        justify-content: center;
      `;

      const printContent = document.createElement("div");
      printContent.className = "print-content";
      printContent.style.cssText = `
        background: white;
        border-radius: 12px;
        padding: 1rem;
        width: 90vw;
        height: 90vh;
        position: relative;
        display: flex;
        flex-direction: column;
      `;

      const printHeader = document.createElement("div");
      printHeader.className = "print-header";
      printHeader.style.cssText = `
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem 1rem;
        background: #f8f9fa;
        border-radius: 8px 8px 0 0;
        border-bottom: 1px solid #dee2e6;
        margin-bottom: 1rem;
      `;

      const printBtn = document.createElement("button");
      printBtn.innerHTML = '<i class="fas fa-print"></i> Print';
      printBtn.style.cssText = `
        padding: 0.5rem 1rem;
        border: none;
        border-radius: 6px;
        background: #007bff;
        color: white;
        cursor: pointer;
        font-size: 0.9rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
      `;

      const closeBtn = document.createElement("button");
      closeBtn.innerHTML = '<i class="fas fa-times"></i>';
      closeBtn.style.cssText = `
        width: 36px;
        height: 36px;
        border: none;
        border-radius: 50%;
        background: #dc3545;
        color: white;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
      `;

      const iframe = document.createElement("iframe");
      iframe.style.cssText = `
        width: 100%;
        height: calc(100% - 80px);
        border: none;
        border-radius: 0 0 8px 8px;
        flex: 1;
      `;

      // Event handlers
      printBtn.onclick = () => {
        try {
          iframe.contentWindow.print();
        } catch (error) {
          console.error('Print error:', error);
          // Fallback: open PDF in new window
          window.open(pdfBlobUrl, '_blank');
        }
      };

      closeBtn.onclick = () => {
        document.body.removeChild(overlay);
        // Clean up blob URL
        URL.revokeObjectURL(pdfBlobUrl);
      };

      // Assemble the modal
      printHeader.appendChild(printBtn);
      printHeader.appendChild(closeBtn);
      printContent.appendChild(printHeader);
      printContent.appendChild(iframe);
      overlay.appendChild(printContent);

      // Add to page
      document.body.appendChild(overlay);

      // Set iframe source after adding to DOM
      iframe.src = pdfBlobUrl;

      // Focus iframe when loaded
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
        } catch (error) {
          // Cannot focus iframe - this is expected in some browsers
        }
      };
    },

    async handleUploadPDF(pdf) {
      const blob = pdf.output("blob");

      // Determine category and remark based on whether this is a template or regular document
      const isTemplate = !!this.templateId;
      const category = "Document"; // Always save signed documents as regular documents
      // เช็ค filename ก่อน isTemplate: เดิมสาขา template ต่อ string ตรง ๆ ทำให้ได้
      // "undefined_signed_by_patient" ถ้าเปิดมาโดยไม่มี query filename
      const remark = this.filename
        ? isTemplate
          ? `${this.filename}_signed_by_patient`
          : `${this.filename}_signed`
        : `Signed_Document_${new Date()
            .toISOString()
            .slice(0, 19)
            .replace(/:/g, "-")}`;

      const { completed } = await this.createMediaFiles({
        clinicUrl: this.clinicUrl,
        branchUrl: this.branchUrl,
        data: {
          category: category,
          patientId: this.patientId ? String(this.patientId) : null,
          remark: remark,
          filesMetadata: [{ contentType: "application/pdf", fileSize: blob.size }],
        },
        files: [blob],
      });

      // Hand the registered file to the patient's tab, which shows it at once
      localStorage.setItem(
        "crossTabEvent",
        JSON.stringify({
          timestamp: Date.now(),
          type: "MEDIA_ADDED",
          payload: {
            patientId: this.patientId,
            files: completed,
            source: "signDoc",
            isTemplateSign: isTemplate,
            templateId: this.templateId,
          },
        })
      );

      // Show success message
      this.$refs.Dialog.showAlertInfo(
        'Document Uploaded',
        isTemplate 
          ? 'Template document has been signed and saved to patient files.'
          : 'Signed document has been uploaded successfully.',
        'success'
      );

      // Close the signing interface after a delay
      setTimeout(() => {
        window.close();
      }, 2000);
    },

    // === CLEANUP ===
    cleanup() {
      this.cleanupDrawing();
      eventBus.$off("renderPDF", this.renderPDF);
      
      // Remove early touch detection
      const container = this.$refs.documentViewer;
      if (container) {
        container.removeEventListener('touchstart', this.detectTouchTypeEarly);
      }
      

      // Cleanup Konva stages
      Object.keys(this.$refs).forEach(refKey => {
        if (refKey.startsWith("stage")) {
          const vueStage = this.$refs[refKey][0];
          if (vueStage && vueStage.getStage) {
            const stage = vueStage.getStage();
            if (stage) {
              stage.destroy();
            }
          }
        }
      });
    },
  },
};
</script>

<style scoped>
.sign-doc-container {
  height: 100vh;
  width: 100vw;
  background: #f5f5f5;
  overflow: auto;
  position: relative;
  touch-action: pan-y pinch-zoom;
}

/* === LOADING STATES === */
.loading-overlay,
.upload-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-content,
.upload-content {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.loading-spinner,
.upload-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e3e3e3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text,
.upload-text {
  margin: 0;
  color: #333;
  font-size: 1.1rem;
  font-weight: 500;
}

/* === DOCUMENT VIEWER === */
.document-viewer {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
}

.pdf-page {
  margin-bottom: 2rem;
  transition: transform 0.2s ease;
  transform-origin: center;
}

.page-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
}

.page-container.allow-touch {
  touch-action: pan-x pan-y pinch-zoom;
}

.page-container.allow-touch .konvajs-content {
  pointer-events: none !important;
}

.page-container.allow-touch .konvajs-content canvas {
  pointer-events: none !important;
}

.page-container:not(.allow-touch) {
  touch-action: none;
}

.page-number {
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
}

/* === FLOATING ACTION BUTTONS === */
.fab-container {
  position: fixed;
  top: 2rem;
  right: 5.5rem;
  display: flex;
  flex-direction: row;
  gap: 1rem;
  z-index: 100;
  align-items: center;
}

.fab-container::after {
  content: '';
  position: absolute;
  right: -2.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 30px;
  background: rgba(0, 0, 0, 0.1);
}

.zoom-controls {
  background: white;
  border-radius: 25px;
  padding: 0.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
}

.zoom-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: #f8f9fa;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
}

.zoom-btn:hover:not(:disabled) {
  background: #007bff;
  color: white;
  transform: scale(1.1);
}

.zoom-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.zoom-level {
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
  min-width: 50px;
  text-align: center;
}

.action-buttons {
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
}

.fab-btn {
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
}

.fab-btn:hover:not(:disabled) {
  transform: scale(1.1);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.3);
}

.fab-btn:active {
  transform: scale(0.95);
}

.fab-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.drawing-mode-switch {
  width: 96px;
  height: 48px;
  background: #f8f9fa;
  border-radius: 24px;
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  border: 2px solid #dee2e6;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.drawing-mode-switch:hover {
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.25);
  transform: scale(1.05);
}

.drawing-mode-switch:active {
  transform: scale(0.98);
}

.switch-option {
  width: 50%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  transition: all 0.3s ease;
  z-index: 2;
  position: relative;
}

.switch-option.active {
  color: white;
}

.switch-option:not(.active) {
  color: #6c757d;
}

.switch-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: calc(50% - 2px);
  height: calc(100% - 4px);
  background: linear-gradient(135deg, #007bff, #0056b3);
  border-radius: 26px;
  transition: all 0.3s ease;
  z-index: 1;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
}

.switch-slider.slide-right {
  left: calc(50% + 2px);
  background: linear-gradient(135deg, #ffc107, #ff8c00);
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
}

.print-btn {
  background: linear-gradient(135deg, #4ecdc4, #44a08d);
  color: white;
}

.upload-btn {
  background: linear-gradient(135deg, #45b7d1, #2196f3);
  color: white;
}

/* === CLOSE BUTTON === */
.close-btn {
  position: fixed;
  top: 2rem;
  right: 2rem;
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  z-index: 100;
}

.close-btn:hover {
  background: rgba(220, 53, 69, 0.9);
  transform: scale(1.1);
}

/* === DRAWING INDICATOR === */
.drawing-indicator {
  position: fixed;
  top: 2rem;
  left: 2rem;
  background: rgba(76, 175, 80, 0.9);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  z-index: 100;
  animation: pulse 2s infinite;
  transition: all 0.3s ease;
}

.drawing-indicator.eraser-mode {
  background: rgba(255, 193, 7, 0.9);
  color: #212529;
}

@keyframes pulse {
  0% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.8;
  }
}


/* === RESPONSIVE DESIGN === */
@media (max-width: 768px) {
  .document-viewer {
    padding: 1rem;
  }

  .fab-container {
    top: 1rem;
    right: 4.5rem;
  }

  .close-btn {
    top: 1rem;
    right: 1rem;
  }

  .drawing-indicator {
    top: 1rem;
    left: 1rem;
    font-size: 0.9rem;
  }

  .zoom-controls {
    flex-wrap: wrap;
  }

  .drawing-mode-switch {
    width: 86px;
    height: 44px;
  }

  .switch-option {
    font-size: 1rem;
  }
}

/* === SMOOTH SCROLLING === */
html {
  scroll-behavior: smooth;
}

/* === KONVA STAGE STYLING === */
.konvajs-content {
  border-radius: 12px;
  overflow: hidden;
}
</style>